import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  ArrowRight, Ban, CheckCircle2, KeyRound, Lock, Unlock, Wallet, ShieldCheck, Send,
  StickyNote, Trash2, Plus, Minus, RefreshCw, ExternalLink, XCircle, Mail,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import {
  Panel, DataTable, Tag, Status, Stat, Drawer, ConfirmButton, Select, RefreshButton,
} from '../ui'
import { get, post, patch, del } from '../../lib/api'
import { toast } from '../../store'
import { clsx, compact, fa, faDate, num, shortAddr, asArray} from '../../lib/format'

const TABS = [
  ['overview', 'نمای کلی'], ['assets', 'دارایی‌ها'], ['orders', 'سفارش‌ها'], ['trades', 'معاملات'],
  ['transactions', 'تراکنش‌ها'], ['chain', 'واریز/برداشت زنجیره'], ['security', 'امنیت و دسترسی'],
  ['notes', 'یادداشت‌ها'],
]

export default function UserDetail() {
  const { id } = useParams()
  const [d, setD] = useState<any>(null)
  const [tab, setTab] = useState('overview')
  const [loading, setLoading] = useState(false)
  const [drawer, setDrawer] = useState<'balance' | 'edit' | 'notify' | 'password' | null>(null)

  const load = () => {
    setLoading(true)
    get(`/admin/users/${id}`).then(setD).catch((e) => toast.err(e.message)).finally(() => setLoading(false))
  }
  useEffect(load, [id])

  if (!d) {
    return (
      <>
        <PageHead title="پرونده کاربر" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="skeleton h-20 w-full" />)}
        </div>
      </>
    )
  }

  const p = d.profile
  const s = d.stats

  const act = async (fn: () => Promise<any>, msg: string) => {
    try { await fn(); toast.ok(msg); load() } catch (e: any) { toast.err(e.message) }
  }

  return (
    <>
      <PageHead
        title={p.fullName || p.email}
        subtitle={`شناسه ${fa(p.id)} · عضویت ${faDate(p.createdAt, false)}`}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/users" className="btn-ghost !py-2 text-xs"><ArrowRight className="h-3.5 w-3.5" /> فهرست</Link>
            <RefreshButton onClick={load} loading={loading} />
          </div>
        }
      />

      {/* ---------- نوار وضعیت و اقدام سریع ---------- */}
      <div className="card mb-5 p-4">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="num text-sm text-slate-200" dir="ltr">{p.email}</span>
          <Tag kind={p.isActive ? 'up' : 'down'}>{p.isActive ? 'فعال' : 'مسدود'}</Tag>
          <Tag kind="info">سطح {fa(p.kycLevel)}</Tag>
          <Tag kind={p.role === 'user' ? 'muted' : 'gold'}>{p.roleLabel}</Tag>
          {p.vipTier > 0 && <Tag kind="gold">VIP {fa(p.vipTier)}</Tag>}
          {p.twoFa && <Tag kind="info">۲FA فعال</Tag>}
          {p.tradeLocked && <Tag kind="gold">قفل معامله</Tag>}
          {p.withdrawLocked && <Tag kind="gold">قفل برداشت</Tag>}
          {p.blockReason && <span className="text-[11px] text-down">دلیل مسدودی: {p.blockReason}</span>}
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={() => setDrawer('edit')} className="btn-ghost !py-2 text-xs">
            <KeyRound className="h-3.5 w-3.5" /> ویرایش پروفایل
          </button>
          <button onClick={() => setDrawer('balance')} className="btn-ghost !py-2 text-xs">
            <Wallet className="h-3.5 w-3.5" /> تغییر موجودی
          </button>
          <button onClick={() => setDrawer('notify')} className="btn-ghost !py-2 text-xs">
            <Send className="h-3.5 w-3.5" /> ارسال پیام
          </button>
          <button onClick={() => setDrawer('password')} className="btn-ghost !py-2 text-xs">
            <Lock className="h-3.5 w-3.5" /> تغییر رمز
          </button>
          <button
            onClick={() => act(() => post(`/admin/users/${id}/locks`, { trade_locked: !p.tradeLocked }),
              'وضعیت قفل معامله تغییر کرد')}
            className="btn-ghost !py-2 text-xs">
            {p.tradeLocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {p.tradeLocked ? 'باز کردن معامله' : 'قفل معامله'}
          </button>
          <button
            onClick={() => act(() => post(`/admin/users/${id}/locks`, { withdraw_locked: !p.withdrawLocked }),
              'وضعیت قفل برداشت تغییر کرد')}
            className="btn-ghost !py-2 text-xs">
            {p.withdrawLocked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            {p.withdrawLocked ? 'باز کردن برداشت' : 'قفل برداشت'}
          </button>
          {p.twoFa && (
            <ConfirmButton variant="ghost" label="بازنشانی ۲FA" question="۲FA بازنشانی شود؟"
              icon={<ShieldCheck className="h-3.5 w-3.5" />}
              onConfirm={() => act(() => post(`/admin/users/${id}/reset-2fa`), 'ورود دومرحله‌ای بازنشانی شد')} />
          )}
          {s.openOrders > 0 && (
            <ConfirmButton variant="ghost" label={`لغو ${fa(s.openOrders)} سفارش باز`}
              icon={<XCircle className="h-3.5 w-3.5" />}
              onConfirm={() => act(() => post(`/admin/users/${id}/cancel-orders`), 'سفارش‌های باز لغو شد')} />
          )}
          <ConfirmButton
            variant={p.isActive ? 'down' : 'up'}
            label={p.isActive ? 'مسدودسازی حساب' : 'فعال‌سازی حساب'}
            question={p.isActive ? 'حساب مسدود شود؟' : 'حساب فعال شود؟'}
            icon={p.isActive ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            onConfirm={() => act(
              () => post(`/admin/users/${id}/status`, {
                active: !p.isActive,
                reason: p.isActive ? 'مسدودسازی توسط مدیر' : '',
              }), 'وضعیت حساب تغییر کرد')}
          />
        </div>
      </div>

      {/* ---------- آمار ---------- */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Stat label="ارزش دارایی" value={compact(s.balanceIrt)} icon={<Wallet className="h-5 w-5" />} />
        <Stat label="حجم معاملات" value={compact(s.volumeIrt)} color="#22D3EE" />
        <Stat label="کارمزد پرداختی" value={compact(s.feesPaidIrt)} color="#00E39A" />
        <Stat label="مجموع واریز" value={compact(s.depositsIrt)} color="#FFB020" />
        <Stat label="مجموع برداشت" value={compact(s.withdrawalsIrt)} color="#FF4D6D" />
      </div>

      {/* ---------- تب‌ها ---------- */}
      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/[.07] bg-white/[.03] p-1">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            className={clsx('rounded-lg px-3 py-2 text-xs font-medium transition',
              tab === k ? 'bg-gradient-to-l from-brand-600 to-brand-400 text-white'
                : 'text-slate-400 hover:bg-white/[.05] hover:text-slate-200')}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview d={d} />}

      {tab === 'assets' && (
        <Panel title="دارایی‌های کاربر" dense>
          <DataTable rows={d.assets} minWidth={600} columns={[
            { key: 'c', header: 'ارز', cell: (r: any) => <span className="text-slate-200">{r.faName} <span className="num text-slate-500">({r.currency})</span></span> },
            { key: 'b', header: 'موجودی', cell: (r: any) => <span className="num text-white">{num(r.balance, 8)}</span> },
            { key: 'l', header: 'در سفارش', cell: (r: any) => <span className="num text-slate-500">{num(r.locked, 8)}</span> },
            { key: 'v', header: 'ارزش (تومان)', cell: (r: any) => <span className="num text-slate-300">{compact(r.valueIrt)}</span> },
          ]} />
        </Panel>
      )}

      {tab === 'orders' && (
        <Panel title="سفارش‌های اخیر" dense>
          <DataTable rows={d.orders} minWidth={720} columns={[
            { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
            { key: 's', header: 'بازار', cell: (r: any) => <span className="num" dir="ltr">{r.symbol}</span> },
            { key: 'side', header: 'سمت', cell: (r: any) => <span className={r.side === 'buy' ? 'text-up' : 'text-down'}>{r.side === 'buy' ? 'خرید' : 'فروش'}</span> },
            { key: 't', header: 'نوع', cell: (r: any) => <span className="text-slate-400">{r.type}</span> },
            { key: 'p', header: 'قیمت', cell: (r: any) => <span className="num text-slate-300">{num(r.price)}</span> },
            { key: 'a', header: 'مقدار', cell: (r: any) => <span className="num text-slate-300">{num(r.amount, 6)}</span> },
            { key: 'st', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
            { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
          ]} />
        </Panel>
      )}

      {tab === 'trades' && (
        <Panel title="معاملات اخیر" dense>
          <DataTable rows={d.trades} minWidth={680} columns={[
            { key: 's', header: 'بازار', cell: (r: any) => <span className="num" dir="ltr">{r.symbol}</span> },
            { key: 'side', header: 'سمت', cell: (r: any) => <span className={r.side === 'buy' ? 'text-up' : r.side === 'sell' ? 'text-down' : 'text-brand-200'}>{r.side === 'buy' ? 'خرید' : r.side === 'sell' ? 'فروش' : 'تبدیل'}</span> },
            { key: 'p', header: 'قیمت', cell: (r: any) => <span className="num text-slate-300">{num(r.price, 6)}</span> },
            { key: 'a', header: 'مقدار', cell: (r: any) => <span className="num text-slate-300">{num(r.amount, 6)}</span> },
            { key: 'tt', header: 'مبلغ کل', cell: (r: any) => <span className="num text-white">{num(r.total)}</span> },
            { key: 'f', header: 'کارمزد', cell: (r: any) => <span className="num text-slate-500">{num(r.fee, 8)}</span> },
            { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
          ]} />
        </Panel>
      )}

      {tab === 'transactions' && (
        <Panel title="تراکنش‌های مالی" dense>
          <DataTable rows={d.transactions} minWidth={800} columns={[
            { key: 'k', header: 'نوع', cell: (r: any) => <span className={r.kind === 'withdraw' ? 'text-down' : r.kind === 'adjustment' ? 'text-gold' : 'text-up'}>{
              { deposit: 'واریز', withdraw: 'برداشت', fee: 'کارمزد', reward: 'پاداش', referral: 'پورسانت', adjustment: 'تعدیل دستی' }[r.kind] ?? r.kind}</span> },
            { key: 'c', header: 'ارز', cell: (r: any) => <span className="num text-slate-300">{r.currency}</span> },
            { key: 'a', header: 'مبلغ', cell: (r: any) => <span className="num text-white">{num(r.amount, 8)}</span> },
            { key: 'st', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
            { key: 'n', header: 'شبکه/مقصد', cell: (r: any) => <span className="num text-slate-500" dir="ltr">{r.network || shortAddr(r.address) || '—'}</span> },
            { key: 'de', header: 'توضیح', cell: (r: any) => <span className="max-w-[16rem] truncate text-slate-400">{r.description}</span> },
            { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
          ]} />
        </Panel>
      )}

      {tab === 'chain' && <ChainTab d={d} id={id!} />}

      {tab === 'security' && <SecurityTab d={d} id={id!} onDone={load} />}

      {tab === 'notes' && <NotesTab d={d} id={id!} onDone={load} />}

      {/* ---------- کشوها ---------- */}
      <BalanceDrawer open={drawer === 'balance'} onClose={() => setDrawer(null)} id={id!}
        assets={d.assets} onDone={load} />
      <EditDrawer open={drawer === 'edit'} onClose={() => setDrawer(null)} id={id!} p={p} onDone={load} />
      <NotifyDrawer open={drawer === 'notify'} onClose={() => setDrawer(null)} id={id!} />
      <PasswordDrawer open={drawer === 'password'} onClose={() => setDrawer(null)} id={id!} />
    </>
  )
}

/* ---------------------------------------------------------------- تب نمای کلی */
function Overview({ d }: { d: any }) {
  const p = d.profile
  const rows: [string, any][] = [
    ['ایمیل', p.email], ['نام و نام خانوادگی', p.fullName || '—'], ['موبایل', p.mobile || '—'],
    ['کد ملی', p.nationalId || '—'], ['کد معرف', p.referralCode],
    ['معرف', p.referrer ? p.referrer.email : '—'],
    ['تعداد زیرمجموعه', fa(p.friendsCount)],
    ['پورسانت دریافتی', compact(p.referralEarnings)],
    ['کارمزد اختصاصی', p.makerFee !== null || p.takerFee !== null
      ? `سازنده ${num((p.makerFee ?? 0) * 100, 3)}٪ / بازارگیر ${num((p.takerFee ?? 0) * 100, 3)}٪` : 'پیش‌فرض سامانه'],
    ['آخرین ورود', p.lastLoginAt ? faDate(p.lastLoginAt) : '—'],
  ]
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Panel title="اطلاعات حساب" className="lg:col-span-2">
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map(([k, v]) => (
            <div key={k} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
              <div className="mb-1 text-[10px] text-slate-500">{k}</div>
              <div className="num truncate text-sm text-slate-200" dir="auto">{v}</div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="کارت‌های بانکی">
        <div className="space-y-2">
          {d.cards.length === 0 && <p className="py-6 text-center text-xs text-slate-500">کارتی ثبت نشده است</p>}
          {d.cards.map((c: any) => (
            <div key={c.id} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs text-slate-300">{c.bankName}</span>
                <Status value={c.status} />
              </div>
              <div className="num text-sm tracking-wider text-white" dir="ltr">{c.cardNumber}</div>
              <div className="num text-[10px] text-slate-500" dir="ltr">{c.iban || '—'}</div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="زیرمجموعه‌ها" className="lg:col-span-3" dense>
        <DataTable rows={d.friends} minWidth={480} empty="زیرمجموعه‌ای ندارد" columns={[
          { key: 'id', header: '#', cell: (r: any) => <Link to={`/admin/users/${r.id}`} className="num text-brand-300">{fa(r.id)}</Link> },
          { key: 'e', header: 'ایمیل', cell: (r: any) => <span className="num" dir="ltr">{r.email}</span> },
          { key: 'k', header: 'سطح', cell: (r: any) => <Tag kind="muted">سطح {fa(r.kycLevel)}</Tag> },
          { key: 'd', header: 'عضویت', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt, false)}</span> },
        ]} />
      </Panel>
    </div>
  )
}

/* ---------------------------------------------------------------- تب زنجیره */
function ChainTab({ d, id }: { d: any; id: string }) {
  const [addrs, setAddrs] = useState<any[]>([])
  useEffect(() => { get(`/admin/users/${id}/deposit-addresses`).then((r: any) => setAddrs(asArray(r))).catch(() => {}) }, [id])
  return (
    <div className="space-y-4">
      <Panel title="آدرس‌های واریز اختصاصی">
        <div className="grid gap-3 sm:grid-cols-2">
          {addrs.length === 0 && <p className="py-4 text-xs text-slate-500">آدرسی ساخته نشده است</p>}
          {addrs.map((a) => (
            <div key={a.family} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
              <div className="mb-1 flex items-center justify-between">
                <Tag kind="info">{a.family.toUpperCase()}</Tag>
                <span className="num text-[10px] text-slate-500">اندیس {fa(a.index)}</span>
              </div>
              <div className="num break-all text-[11px] text-slate-300" dir="ltr">{a.address}</div>
              {a.explorer && (
                <a href={a.explorer} target="_blank" rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-300 hover:underline">
                  اکسپلورر <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="واریزهای زنجیره‌ای" dense>
        <DataTable rows={d.deposits} minWidth={720} empty="واریزی ثبت نشده است" columns={[
          { key: 'n', header: 'شبکه', cell: (r: any) => <Tag kind="muted">{r.network}</Tag> },
          { key: 's', header: 'ارز', cell: (r: any) => <span className="num text-slate-200">{r.symbol}</span> },
          { key: 'a', header: 'مقدار', cell: (r: any) => <span className="num text-white">{num(r.amount, 8)}</span> },
          { key: 'c', header: 'تأیید', cell: (r: any) => <span className="num text-slate-400">{fa(r.confirmations)}/{fa(r.required)}</span> },
          { key: 'st', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
          { key: 'tx', header: 'تراکنش', cell: (r: any) => <span className="num text-slate-500" dir="ltr">{shortAddr(r.txid, 8)}</span> },
          { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
        ]} />
      </Panel>

      <Panel title="برداشت‌های زنجیره‌ای" dense>
        <DataTable rows={d.withdrawals} minWidth={780} empty="برداشتی ثبت نشده است" columns={[
          { key: 'n', header: 'شبکه', cell: (r: any) => <Tag kind="muted">{r.network}</Tag> },
          { key: 's', header: 'ارز', cell: (r: any) => <span className="num text-slate-200">{r.symbol}</span> },
          { key: 'a', header: 'مقدار', cell: (r: any) => <span className="num text-white">{num(r.amount, 8)}</span> },
          { key: 'to', header: 'مقصد', cell: (r: any) => <span className="num text-slate-500" dir="ltr">{shortAddr(r.toAddress, 8)}</span> },
          { key: 'st', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
          { key: 'e', header: 'خطا', cell: (r: any) => <span className="max-w-[12rem] truncate text-[11px] text-down">{r.error || '—'}</span> },
          { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
        ]} />
      </Panel>
    </div>
  )
}

/* ---------------------------------------------------------------- تب امنیت */
function SecurityTab({ d, id, onDone }: { d: any; id: string; onDone: () => void }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="کلیدهای API" dense>
        <DataTable rows={d.apiKeys} minWidth={420} empty="کلیدی ساخته نشده است" columns={[
          { key: 'l', header: 'عنوان', cell: (r: any) => <span className="text-slate-200">{r.label}</span> },
          { key: 'k', header: 'کلید', cell: (r: any) => <span className="num text-slate-500" dir="ltr">{shortAddr(r.key, 10)}</span> },
          { key: 'p', header: 'دسترسی', cell: (r: any) => <Tag kind={r.permissions === 'read' ? 'muted' : 'gold'}>{r.permissions === 'read' ? 'خواندن' : 'معامله'}</Tag> },
          {
            key: 'x', header: '', cell: (r: any) => (
              <ConfirmButton variant="ghost" label="حذف" question="حذف شود؟"
                icon={<Trash2 className="h-3.5 w-3.5" />}
                onConfirm={async () => {
                  try { await del(`/admin/users/${id}/api-keys/${r.id}`); toast.ok('کلید حذف شد'); onDone() }
                  catch (e: any) { toast.err(e.message) }
                }} />
            ),
          },
        ]} />
      </Panel>

      <Panel title="تاریخچه ورود" dense>
        <DataTable rows={d.logins} minWidth={420} empty="ورودی ثبت نشده است" columns={[
          { key: 'ip', header: 'IP', cell: (r: any) => <span className="num text-slate-300" dir="ltr">{r.ip || '—'}</span> },
          { key: 'a', header: 'دستگاه', cell: (r: any) => <span className="block max-w-[18rem] truncate text-[11px] text-slate-500" dir="ltr">{r.agent || '—'}</span> },
          { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.at)}</span> },
        ]} />
      </Panel>

      <Panel title="تیکت‌های پشتیبانی" dense className="lg:col-span-2">
        <DataTable rows={d.tickets} minWidth={480} empty="تیکتی ثبت نشده است" columns={[
          { key: 'id', header: '#', cell: (r: any) => <span className="num text-slate-500">{fa(r.id)}</span> },
          { key: 's', header: 'موضوع', cell: (r: any) => <span className="text-slate-200">{r.subject}</span> },
          { key: 'st', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
          { key: 'd', header: 'زمان', cell: (r: any) => <span className="text-slate-500">{faDate(r.createdAt)}</span> },
        ]} />
      </Panel>
    </div>
  )
}

/* ---------------------------------------------------------------- تب یادداشت */
function NotesTab({ d, id, onDone }: { d: any; id: string; onDone: () => void }) {
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)

  const add = async () => {
    if (body.trim().length < 3) return toast.err('متن یادداشت کوتاه است')
    setSaving(true)
    try { await post(`/admin/users/${id}/notes`, { body }); setBody(''); toast.ok('یادداشت ثبت شد'); onDone() }
    catch (e: any) { toast.err(e.message) } finally { setSaving(false) }
  }

  return (
    <Panel title="یادداشت‌های داخلی" action={<span className="text-[11px] text-slate-500">فقط برای کارکنان قابل مشاهده است</span>}>
      <div className="mb-4">
        <textarea className="input min-h-[90px]" placeholder="یادداشت جدید…"
          value={body} onChange={(e) => setBody(e.target.value)} />
        <button onClick={add} disabled={saving} className="btn-primary mt-2 !py-2 text-xs">
          <StickyNote className="h-3.5 w-3.5" /> ثبت یادداشت
        </button>
      </div>
      <div className="space-y-2">
        {d.notes.length === 0 && <p className="py-6 text-center text-xs text-slate-500">یادداشتی ثبت نشده است</p>}
        {d.notes.map((n: any) => (
          <div key={n.id} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
            <div className="mb-1.5 flex items-center gap-2 text-[11px] text-slate-500">
              <span className="num" dir="ltr">{n.admin}</span>
              <span>·</span>
              <span>{faDate(n.createdAt)}</span>
              <button
                onClick={async () => {
                  try { await del(`/admin/users/${id}/notes/${n.id}`); toast.ok('حذف شد'); onDone() }
                  catch (e: any) { toast.err(e.message) }
                }}
                className="mr-auto rounded p-1 hover:bg-down/10 hover:text-down">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="whitespace-pre-wrap text-xs leading-6 text-slate-300">{n.body}</p>
          </div>
        ))}
      </div>
    </Panel>
  )
}

/* ---------------------------------------------------------------- کشوها */
function BalanceDrawer({ open, onClose, id, assets, onDone }:
  { open: boolean; onClose: () => void; id: string; assets: any[]; onDone: () => void }) {
  const [form, setForm] = useState({ currency: 'IRT', amount: '', direction: 'credit', reason: '' })
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    setSaving(true)
    try {
      await post(`/admin/users/${id}/balance`, { ...form, amount: Number(form.amount) })
      toast.ok('موجودی به‌روزرسانی شد')
      setForm({ ...form, amount: '', reason: '' })
      onDone(); onClose()
    } catch (e: any) { toast.err(e.message) } finally { setSaving(false) }
  }

  return (
    <Drawer open={open} onClose={onClose} title="تغییر دستی موجودی">
      <div className="mb-4 rounded-xl border border-gold/25 bg-gold/[.07] p-3 text-[11px] leading-6 text-gold">
        هر تغییر دستی در گزارش اقدامات مدیران ثبت و به کاربر اطلاع داده می‌شود.
      </div>
      <label className="label">ارز</label>
      <select className="input mb-3" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
        {['IRT', ...new Set(assets.map((a: any) => a.currency))].filter((v, i, arr) => arr.indexOf(v) === i)
          .map((c: any) => <option key={c} value={c} className="bg-ink-900">{c}</option>)}
      </select>

      <label className="label">نوع تغییر</label>
      <div className="mb-3 grid grid-cols-2 gap-2">
        {[['credit', 'افزایش', Plus], ['debit', 'کاهش', Minus]].map(([v, l, Icon]: any) => (
          <button key={v} onClick={() => setForm({ ...form, direction: v })}
            className={clsx('flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs transition',
              form.direction === v
                ? v === 'credit' ? 'border-up/50 bg-up/10 text-up' : 'border-down/50 bg-down/10 text-down'
                : 'border-white/10 text-slate-400')}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>

      <label className="label">مبلغ</label>
      <input className="input num mb-3" dir="ltr" value={form.amount}
        onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/[^\d.]/g, '') })} />

      <label className="label">دلیل (الزامی)</label>
      <textarea className="input mb-4 min-h-[70px]" value={form.reason}
        onChange={(e) => setForm({ ...form, reason: e.target.value })}
        placeholder="مثلاً جبران خطای واریز شماره ۱۲۳" />

      <button onClick={submit} disabled={saving || !Number(form.amount) || form.reason.trim().length < 3}
        className="btn-primary w-full !py-3">اعمال تغییر</button>
    </Drawer>
  )
}

function EditDrawer({ open, onClose, id, p, onDone }:
  { open: boolean; onClose: () => void; id: string; p: any; onDone: () => void }) {
  const [f, setF] = useState<any>({})
  useEffect(() => {
    setF({
      full_name: p.fullName, mobile: p.mobile, national_id: p.nationalId, email: p.email,
      kyc_level: p.kycLevel, role: p.role, vip_tier: p.vipTier,
      maker_fee: p.makerFee ?? '', taker_fee: p.takerFee ?? '', tags: (p.tags || []).join(','),
    })
  }, [p, open])

  const save = async () => {
    const body: any = { ...f }
    body.maker_fee = f.maker_fee === '' ? null : Number(f.maker_fee)
    body.taker_fee = f.taker_fee === '' ? null : Number(f.taker_fee)
    Object.keys(body).forEach((k) => body[k] === null && delete body[k])
    try { await patch(`/admin/users/${id}`, body); toast.ok('ذخیره شد'); onDone(); onClose() }
    catch (e: any) { toast.err(e.message) }
  }

  const field = (key: string, label: string, extra: any = {}) => (
    <div className="mb-3">
      <label className="label">{label}</label>
      <input className="input" value={f[key] ?? ''} onChange={(e) => setF({ ...f, [key]: e.target.value })} {...extra} />
    </div>
  )

  return (
    <Drawer open={open} onClose={onClose} title="ویرایش پروفایل کاربر">
      {field('full_name', 'نام و نام خانوادگی')}
      {field('email', 'ایمیل', { dir: 'ltr', className: 'input num' })}
      {field('mobile', 'موبایل', { dir: 'ltr', className: 'input num' })}
      {field('national_id', 'کد ملی', { dir: 'ltr', className: 'input num' })}

      <label className="label">سطح احراز هویت</label>
      <select className="input mb-3" value={f.kyc_level}
        onChange={(e) => setF({ ...f, kyc_level: Number(e.target.value) })}>
        {[0, 1, 2, 3].map((l) => <option key={l} value={l} className="bg-ink-900">سطح {l}</option>)}
      </select>

      <label className="label">نقش (فقط مالک سامانه می‌تواند تغییر دهد)</label>
      <select className="input mb-3" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
        {[['user', 'کاربر عادی'], ['support', 'پشتیبانی'], ['finance', 'کارشناس مالی'],
          ['admin', 'مدیر کل'], ['owner', 'مالک سامانه']].map(([v, l]) =>
          <option key={v} value={v} className="bg-ink-900">{l}</option>)}
      </select>

      <label className="label">سطح VIP</label>
      <input className="input num mb-3" dir="ltr" value={f.vip_tier ?? 0}
        onChange={(e) => setF({ ...f, vip_tier: Number(e.target.value.replace(/\D/g, '') || 0) })} />

      <div className="mb-3 grid grid-cols-2 gap-2">
        <div>
          <label className="label">کارمزد سازنده</label>
          <input className="input num" dir="ltr" placeholder="پیش‌فرض" value={f.maker_fee}
            onChange={(e) => setF({ ...f, maker_fee: e.target.value })} />
        </div>
        <div>
          <label className="label">کارمزد بازارگیر</label>
          <input className="input num" dir="ltr" placeholder="پیش‌فرض" value={f.taker_fee}
            onChange={(e) => setF({ ...f, taker_fee: e.target.value })} />
        </div>
      </div>

      {field('tags', 'برچسب‌ها (با کاما جدا کنید)')}

      <div className="flex gap-2">
        <button onClick={save} className="btn-primary flex-1 !py-3">ذخیره تغییرات</button>
        <button onClick={async () => {
          try { await patch(`/admin/users/${id}`, { clear_fees: true }); toast.ok('کارمزد اختصاصی حذف شد'); onDone() }
          catch (e: any) { toast.err(e.message) }
        }} className="btn-ghost !py-3 text-xs">حذف کارمزد اختصاصی</button>
      </div>
    </Drawer>
  )
}

function NotifyDrawer({ open, onClose, id }: { open: boolean; onClose: () => void; id: string }) {
  const [f, setF] = useState({ title: '', body: '', kind: 'info' })
  return (
    <Drawer open={open} onClose={onClose} title="ارسال پیام به کاربر">
      <label className="label">عنوان</label>
      <input className="input mb-3" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <label className="label">متن پیام</label>
      <textarea className="input mb-3 min-h-[100px]" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
      <label className="label">نوع</label>
      <select className="input mb-4" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
        {[['info', 'اطلاع‌رسانی'], ['success', 'موفقیت'], ['warning', 'هشدار'], ['danger', 'مهم']].map(([v, l]) =>
          <option key={v} value={v} className="bg-ink-900">{l}</option>)}
      </select>
      <button onClick={async () => {
        try { await post(`/admin/users/${id}/notify`, f); toast.ok('پیام ارسال شد'); onClose() }
        catch (e: any) { toast.err(e.message) }
      }} disabled={!f.title} className="btn-primary w-full !py-3">
        <Mail className="h-4 w-4" /> ارسال
      </button>
    </Drawer>
  )
}

function PasswordDrawer({ open, onClose, id }: { open: boolean; onClose: () => void; id: string }) {
  const [pw, setPw] = useState('')
  return (
    <Drawer open={open} onClose={onClose} title="تغییر رمز عبور کاربر">
      <div className="mb-4 rounded-xl border border-down/25 bg-down/[.07] p-3 text-[11px] leading-6 text-down">
        این اقدام در گزارش ثبت می‌شود و به کاربر اطلاع داده می‌شود. رمز جدید را از کانال امن به کاربر بدهید.
      </div>
      <label className="label">رمز جدید (حداقل ۸ کاراکتر)</label>
      <input className="input num mb-4" dir="ltr" value={pw} onChange={(e) => setPw(e.target.value)} />
      <button onClick={async () => {
        try { await post(`/admin/users/${id}/password`, { password: pw }); toast.ok('رمز تغییر کرد'); setPw(''); onClose() }
        catch (e: any) { toast.err(e.message) }
      }} disabled={pw.length < 8} className="btn-primary w-full !py-3">تغییر رمز</button>
    </Drawer>
  )
}
